import { Flex, Heading } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import HealthSummarySectionCard from "./HealthSummarySectionCard";
import { useUserQuery } from "../common/useUserQuery";
import { useMedicationsQuery } from "./api/useMedicationsQuery";
import React, { useMemo } from "react";
import { useHRImagingAndLabsDocumentsQuery } from "./api/useImagingAndLabsQuery";
import { useMedicalConditionsQuery } from "./api/useMedicalConditionQuery";
import { useSymptomsQuery } from "./api/useSymptomsQuery";
import { useAllergiesQuery } from "./api/useAllergiesQuery";
import { useImmunizationsQuery } from "./api/useImmunizationsQuery";
import { useLifeStyleQuery } from "../lifestyle/api/useLifeStyleQuery";
import { useVitalsQuery } from "./api/useVitalsQuery";
import { Grid, View, useTheme } from "@aws-amplify/ui-react";

const HealthSummaryPage = () => {
  const { tokens } = useTheme();
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const { data: medicationsData } = useMedicationsQuery(
    userQueryData?.patientUserId
  );
  const { data: documentResults } = useHRImagingAndLabsDocumentsQuery(
    userQueryData?.patientUserId
  );
  const { data: medicalConditionResults } = useMedicalConditionsQuery(
    userQueryData?.patientUserId
  );
  const { data: symptomResults } = useSymptomsQuery(
    userQueryData?.patientUserId
  );
  const { data: allergyData } = useAllergiesQuery(userQueryData?.patientUserId);
  const { data: immunizationResults } = useImmunizationsQuery(
    userQueryData?.patientUserId
  );
  const { data: lifestyleData } = useLifeStyleQuery(
    userQueryData?.patientUserId
  );
  const { data: vitalsData } = useVitalsQuery(userQueryData?.patientUserId);

  const mostRecentMedicationUpdatedAt = useMemo(() => {
    if (medicationsData && medicationsData.length > 0) {
      const mostRecent = medicationsData.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [medicationsData]);

  const mostRecentDocumentUpdatedAt = useMemo(() => {
    if (documentResults && documentResults.length > 0) {
      const mostRecent = documentResults.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [documentResults]);

  const mostRecentMedicalConditionUpdatedAt = useMemo(() => {
    if (medicalConditionResults && medicalConditionResults.length > 0) {
      const mostRecent = medicalConditionResults.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [medicalConditionResults]);

  const mostRecentSymptomsUpdatedAt = useMemo(() => {
    if (symptomResults && symptomResults.length > 0) {
      const mostRecent = symptomResults.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [symptomResults]);

  const mostRecentAllergyUpdatedAt = useMemo(() => {
    if (allergyData && allergyData.length > 0) {
      const mostRecent = allergyData.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [allergyData]);

  const mostRecentImmunizationUpdatedAt = useMemo(() => {
    if (immunizationResults && immunizationResults.length > 0) {
      const mostRecent = immunizationResults.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [immunizationResults]);

  const mostRecentVitalsUpdatedAt = useMemo(() => {
    if (vitalsData && vitalsData.length > 0) {
      const mostRecent = vitalsData.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      return mostRecent?.updatedAt;
    } else {
      return null;
    }
  }, [vitalsData]);

  return (
    <Flex
      direction="column"
      padding="32px 32px 32px 32px"
      backgroundColor="rgba(250,250,250,1)"
    >
      <Flex direction="row" alignItems="center">
        <Heading level="1" children="Health Records"></Heading>
      </Flex>
      <Grid
        templateColumns={{ medium: "1fr 1fr 1fr" }}
        gap={tokens.space.small}
      >
        <HealthSummarySectionCard
          label="Imaging and Lab Results"
          updatedAt={mostRecentDocumentUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/documents/")}
        />
        <HealthSummarySectionCard
          label="Medical Conditions"
          updatedAt={mostRecentMedicalConditionUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/medical-problems/")}
        />
        <HealthSummarySectionCard
          label="Symptoms"
          updatedAt={mostRecentSymptomsUpdatedAt || ""}
          onViewClick={() => navigate("/user/quick-actions/overall-symptoms")}
        />

        <HealthSummarySectionCard
          label="Allergies"
          updatedAt={mostRecentAllergyUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/allergies/")}
        />

        <HealthSummarySectionCard
          label="Vitals"
          updatedAt={mostRecentVitalsUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/vitals/")}
        />

        <HealthSummarySectionCard
          label="Medication and Supplements"
          updatedAt={mostRecentMedicationUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/medications/")}
        />

        <HealthSummarySectionCard
          label="Immunizations"
          updatedAt={mostRecentImmunizationUpdatedAt || ""}
          onViewClick={() => navigate("/user/hr/immunizations/")}
        />

        <HealthSummarySectionCard
          label="Lifestyle"
          updatedAt={lifestyleData?.updatedAt || ""}
          onViewClick={() => navigate("/user/hr/lifestyle/")}
        />
      </Grid>
    </Flex>
  );
};

export default HealthSummaryPage;
